import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useStateWithDebounce, useAuth, CompanyMemberRole } from '@modmd/data'
import { isNil, pickBy } from 'lodash'
import { COLOR } from 'theme'
import { Container } from 'components/Layout'
import Button from 'components/Button'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import Datalist from 'components/Datalist'
import { CompanyGroupsDatalist } from 'sharedComponents/CompanyGroupsDatalist'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { CreateNewGroupDrawer } from 'sharedComponents/CreateNewGroupDrawer'
import { COMPANY_ROUTES, ROUTES } from 'client-portal/constants/routes'

interface RouterProps {
  companyId: string
}

export enum FormName {
  NAME_LIKE = 'nameLike',
}

export enum PaginationProps {
  PAGE = 'page',
}

const CompanyGroups: React.VFC<RouteComponentProps<RouterProps>> = ({ match, history }) => {
  const { getUserCompaniesWithRole } = useAuth()
  const { hasCompaniesWithRole } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Owner],
  })
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const searchState = useSearchState<{
    [FormName.NAME_LIKE]?: string
    [PaginationProps.PAGE]?: string
  }>()
  const [nameLike, setNameLike] = useStateWithDebounce(
    searchState.data[FormName.NAME_LIKE] || '',
    (nameLike) => {
      searchState.setSearchState({ [FormName.NAME_LIKE]: nameLike, page: 1 }, false, true)
    }
  )
  const sessionsFilter = React.useMemo(() => {
    const { nameLike } = searchState.data
    return pickBy({ nameLike }, (value) => !isNil(value))
  }, [searchState])

  return (
    <CompanyPageLayout>
      <Container>
        <CompanyGroupsDatalist
          tableHeader={
            <Datalist.Title
              rightContent={
                <>
                  <TextInput
                    name={FormName.NAME_LIKE}
                    placeholder="Search"
                    value={nameLike}
                    rightIcon={<Icon.Search size="L" color={COLOR.gray} />}
                    onChange={(event) => setNameLike(event.target.value)}
                    hasBottomContent={false}
                    hasTopContent={false}
                  />
                  {hasCompaniesWithRole && (
                    <Button leftIcon={<Icon.Plus size="L" />} onClick={() => setIsDialogOpen(true)}>
                      Add new
                    </Button>
                  )}
                </>
              }
            >
              Company Groups
            </Datalist.Title>
          }
          filter={sessionsFilter}
          sorter={undefined}
          page={
            searchState.data[PaginationProps.PAGE]
              ? Number(searchState.data[PaginationProps.PAGE])
              : 1
          }
          onPageChange={(page) => searchState.setSearchState({ page }, false)}
          onRowClick={({ id }) =>
            history.push(
              `/${ROUTES.COMPANY}/${match.params.companyId}/${COMPANY_ROUTES.GROUPS}/${id}`
            )
          }
        />
      </Container>
      <CreateNewGroupDrawer
        companyId={match.params.companyId}
        isOpen={isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
        onGroupCreated={() => setIsDialogOpen(false)}
        queriesToRefetch={['Groups']}
        isClient
      />
    </CompanyPageLayout>
  )
}

export { CompanyGroups }
